import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { type ListSigningDto } from 'xosign-types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useApp from '@/pages/App.context';
import useApi from '@/hooks/useApi';
import Button from '@/components/Button';
import ModalSidebar from '@/components/ModalSidebar';
import StatusBanner, { type StatusBannerKind } from '@/components/StatusBanner';
import SuccessBanner from '@/components/SuccessBanner';
import useListingFunctions from '@/hooks/useListingFunctions';
import IcFileStack from '@/assets/icons/FileStack.svg?react';
import IcMagnifyingGlass from '@/assets/icons/MagnifyingGlass.svg?react';
import IcPlus from '@/assets/icons/Plus.svg?react';
import IcTrash from '@/assets/icons/Trash.svg?react';
import Listing from './Listing';
import SendProgressBanner from './SendProgressBanner';
import FilterPills, {
  computeCounts,
  matchesFilters,
  SIGNING_FILTERS,
  type SigningFilter,
} from './FilterPills';
import styles from './Signings.module.scss';
import preloadChunks from '@/routes/preloadChunks.util';

interface SigningsProps {
  filter?: SigningFilter;
}

const SEARCH_KEYS: (keyof ListSigningDto)[] = ['name'];

const parseFiltersFromQuery = (raw: string | null): SigningFilter[] => {
  if (!raw) {
    return [];
  }
  const tokens = raw.split(',').map((s) => s.trim());
  return SIGNING_FILTERS.filter((f) => tokens.includes(f));
};

const Signings = ({ filter }: SigningsProps) => {
  const { setAlertDialog, setAppTitle, setHeadline } = useApp();
  const { t } = useTranslation(['common', 'signings']);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadSignings, { response, loading, error }] =
    useApi('signings.get.all');
  const [deleteSigning] = useApi('signings.delete');

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [showDeleteBanner, setShowDeleteBanner] = useState<boolean>(false);
  const [deletedName, setDeletedName] = useState<string>('');
  const [deleteError, setDeleteError] = useState<{
    kind: StatusBannerKind;
    message: string;
  } | null>(null);

  const performDelete = async (signing: ListSigningDto) => {
    setDeletingIds((prev) => [...prev, signing.id]);
    const result = await deleteSigning({ workflowId: signing.id });
    setDeletingIds((prev) => prev.filter((id) => id !== signing.id));

    if (result?.response?.success) {
      setDeletedName(signing.name);
      setShowDeleteBanner(true);
      setDeleteError(null);
      void loadSignings();
    } else {
      setDeleteError({
        kind: 'error',
        message: t('signings:delete.failed'),
      });
    }
  };

  const handleDeleteRequest = (signing: ListSigningDto) => {
    setAlertDialog({
      icon: IcTrash,
      iconVariant: 'danger',
      title: t('signings:delete.confirmTitle'),
      desc: t('signings:deleteDesc', { count: 1 }),
      entityLabel: signing.name,
      acceptLabel: t('signings:buttons.delete'),
      acceptVariant: 'alert',
      declineLabel: t('common:button.cancel'),
      onAccept: () => performDelete(signing),
    });
  };

  const handlePreloadSigning = () => {
    void preloadChunks['signings.workflow']();
  };
  const handlePreloadTemplates = () => {
    void preloadChunks['templates.workflow']();
  };

  // Capture cancel-success state once on first mount so dismissing doesn't
  // fight a router re-render (mirrors the justSubscribed banner on
  // /account/subscription).
  const cancelState = (location.state ?? {}) as {
    justCancelled?: boolean;
    cancelledName?: string;
  };
  const [showCancelBanner, setShowCancelBanner] = useState(
    !!cancelState.justCancelled
  );
  const [cancelledName] = useState(cancelState.cancelledName ?? '');

  // The just-sent banner cannot use the capture-once approach: the workflow
  // editor is a child route rendered in the ModalSidebar over this page, so
  // DialogSend's navigate lands here WITHOUT remounting and a mount-time
  // initializer would miss it. Derive from the live router state instead;
  // dismissal is tracked per workflow id so a later send shows again.
  const sendState = (location.state ?? {}) as {
    justStarted?: boolean;
    startedWorkflowId?: string;
    startedName?: string;
    startedWithReviewers?: boolean;
  };
  const [dismissedSendBannerIds, setDismissedSendBannerIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    const routerState = (location.state ?? {}) as { justStarted?: boolean };
    if (!routerState.justStarted) {
      return;
    }
    // The send flag is one-shot: strip it from the persisted history entry
    // so a page refresh cannot replay the banner after preparation finished
    // (history.state survives reloads). The router's in-memory location
    // keeps the flag for the current visit; refreshes mid-preparation are
    // covered by the list-derived banners below.
    const historyState = (window.history.state ?? {}) as { usr?: unknown };
    if (historyState.usr) {
      window.history.replaceState({ ...historyState, usr: undefined }, '');
    }
  }, [location.state]);

  const queryFilters = parseFiltersFromQuery(searchParams.get('status'));
  const activeFilters: SigningFilter[] =
    queryFilters.length > 0 ? queryFilters : filter ? [filter] : [];

  const baseRoute = filter ? `/signings/${filter}` : '/signings';

  const { handleDetailClose, searchAndSort } =
    useListingFunctions<ListSigningDto>({
      baseRoute,
      defaultSortBy: 'updatedAt',
      fixedSortDir: 'desc',
      onClose: () => {
        void loadSignings();
      },
      searchKeys: SEARCH_KEYS,
      searchTerm,
    });

  const handleToggleFilter = (toggled: 'all' | SigningFilter) => {
    if (toggled === 'all') {
      void navigate('/signings');
      return;
    }
    const next = activeFilters.includes(toggled)
      ? activeFilters.filter((f) => f !== toggled)
      : [...activeFilters, toggled];

    if (next.length === 0) {
      void navigate('/signings');
      return;
    }
    if (next.length === 1) {
      void navigate(`/signings/${next[0]}`);
      return;
    }
    const sorted = SIGNING_FILTERS.filter((f) => next.includes(f));
    void navigate(`/signings?status=${sorted.join(',')}`);
  };

  const subtitleKey: string = (() => {
    if (activeFilters.length === 0) {
      return 'signings:title.all';
    }
    if (activeFilters.length === 1) {
      return `signings:title.${activeFilters[0]}`;
    }
    return 'signings:title.filtered';
  })();

  useEffect(() => {
    const documentsLabel = t('common:menu.signings');
    const subtitle = t(subtitleKey);
    setAppTitle(`${documentsLabel}: ${subtitle}`);
    setHeadline({
      inline: true,
      title: documentsLabel,
      subtitle,
    });
  }, [setAppTitle, setHeadline, t, subtitleKey]);

  useEffect(() => {
    return () => {
      setHeadline(null);
    };
  }, [setHeadline]);

  useEffect(() => {
    void loadSignings();
  }, [loadSignings]);

  const allSignings = response?.data ?? [];
  const counts = computeCounts(allSignings);

  // Every STARTING signing in the fresh list keeps a banner alive, so the
  // progress survives leaving and revisiting the page mid-preparation (the
  // router state is lost then) and multiple sends each get their own banner.
  // Gated on `loading === false` so a stale cached list can't flash banners
  // for signings that already finished preparing.
  const preparingFromList =
    loading === false
      ? allSignings
          .filter((signing) => signing.state === 'STARTING')
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      : [];

  const sendBannerSignings = (() => {
    const items: {
      workflowId: string;
      name: string;
      hasReviewers: boolean;
    }[] = [];
    if (sendState.justStarted && sendState.startedWorkflowId) {
      items.push({
        workflowId: sendState.startedWorkflowId,
        name: sendState.startedName ?? '',
        hasReviewers: !!sendState.startedWithReviewers,
      });
    }
    for (const signing of preparingFromList) {
      if (!items.some((item) => item.workflowId === signing.id)) {
        items.push({
          workflowId: signing.id,
          name: signing.name,
          hasReviewers: false,
        });
      }
    }
    return items.filter(
      (item) => !dismissedSendBannerIds.includes(item.workflowId)
    );
  })();

  const filtered = allSignings.filter((signing) =>
    matchesFilters(signing, activeFilters)
  );
  const signings = searchAndSort(filtered);

  const loadStateRef = useRef({ response, error });

  useEffect(() => {
    loadStateRef.current = { response, error };
  }, [response, error]);

  useEffect(() => {
    if (error) {
      toast.error(t('signings:error.failedToLoad'));
    }
  }, [error, t]);

  useEffect(() => {
    // since 'done' has only 4 chars, use only the last 4 of every possible route
    const allowedPaths = [
      'rail', // audit-trail
      'mary', // summary
      'port', // report
      'ings', // signings
      'raft', // draft
      'ding', // pending
      'ired', // expired
      'lled', // cancelled
      'done', // done
    ];

    if (
      location.state?.refreshOnSameClick &&
      allowedPaths.includes(location.pathname.slice(-4)) &&
      (loadStateRef.current.response?.data || loadStateRef.current.error)
    ) {
      void loadSignings();
    }
  }, [loadSignings, location.state?.refreshOnSameClick, location.pathname]);

  const sectionLabel: string = (() => {
    if (activeFilters.length === 0) {
      return t('signings:title.allDocuments');
    }
    if (activeFilters.length === 1) {
      return t(`signings:title.${activeFilters[0]}`);
    }
    return t('signings:title.filtered');
  })();
  const listingType: 'all' | SigningFilter | 'multi' = (() => {
    if (activeFilters.length === 0) {
      return 'all';
    }
    if (activeFilters.length === 1) {
      return activeFilters[0];
    }
    return 'multi';
  })();

  return (
    <div className={styles.pane}>
      {sendBannerSignings.map((signing) => (
        <div key={signing.workflowId} className={styles.cancelBanner}>
          <SendProgressBanner
            workflowId={signing.workflowId}
            name={signing.name}
            hasReviewers={signing.hasReviewers}
            onDismiss={() =>
              setDismissedSendBannerIds((prev) => [...prev, signing.workflowId])
            }
          />
        </div>
      ))}
      {showCancelBanner && (
        <div className={styles.cancelBanner}>
          <SuccessBanner
            title={t('signings:cancelProcess.successBanner.title')}
            subtitle={t('signings:cancelProcess.successBanner.subtitle', {
              name: cancelledName,
            })}
            dismissLabel={t('signings:cancelProcess.successBanner.dismiss')}
            onDismiss={() => setShowCancelBanner(false)}
          />
        </div>
      )}
      {showDeleteBanner && (
        <div className={styles.cancelBanner}>
          <SuccessBanner
            title={t('signings:delete.successBanner.title')}
            subtitle={t('signings:delete.successBanner.subtitle', {
              name: deletedName,
            })}
            dismissLabel={t('signings:delete.successBanner.dismiss')}
            onDismiss={() => setShowDeleteBanner(false)}
          />
        </div>
      )}
      {deleteError && (
        <div className={styles.cancelBanner}>
          <StatusBanner
            kind={deleteError.kind}
            message={deleteError.message}
            dismissLabel={t('common:dialog.decline')}
            onDismiss={() => setDeleteError(null)}
          />
        </div>
      )}
      <div className={styles.toolbar}>
        <label className={styles.search}>
          <IcMagnifyingGlass
            viewBox="0 0 24 24"
            className={styles.searchIcon}
            aria-hidden="true"
          />
          <input
            type="search"
            className={styles.searchInput}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('common:search.placeholder')}
            aria-label={t('common:search.label')}
          />
        </label>
        <Button
          as="link"
          to="new"
          className={styles.addNewButton}
          variant="secondary"
          size="slim"
          onMouseEnter={handlePreloadSigning}
          onFocus={handlePreloadSigning}
        >
          <IcPlus className={styles.addNewIcon} aria-hidden="true" />
          {t('signings:buttons.new')}
        </Button>
        <Button
          as="link"
          to="/templates/new"
          className={styles.addNewTemplate}
          variant="text"
          size="slim"
          ariaLabel={t('signings:buttons.newTemplate')}
          onMouseEnter={handlePreloadTemplates}
          onFocus={handlePreloadTemplates}
        >
          <IcFileStack className={styles.addNewIcon} aria-hidden="true" />
          <span className={styles.addNewTemplateLabel}>
            {t('signings:buttons.newTemplate')}
          </span>
        </Button>
      </div>

      <FilterPills
        activeFilters={activeFilters}
        counts={counts}
        onToggle={handleToggleFilter}
      />

      <Listing
        className={styles.listing}
        deletingIds={deletingIds}
        hasSearchTerm={searchTerm.length > 0}
        label={sectionLabel}
        loading={loading}
        onDelete={handleDeleteRequest}
        signings={signings}
        type={listingType}
      />

      <ModalSidebar name="SigningsDetail" onClose={handleDetailClose} />
    </div>
  );
};

export default Signings;
